import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class MemoizationService {
  private cache = new Map<string, { value: any; timestamp: number; ttl: number }>();

  /**
   * Mémoïse une fonction avec une clé et une durée de vie
   */
  memoize<T>(key: string, fn: () => T, ttl: number = 300000): T { // 5 minutes par défaut
    const now = Date.now();
    const cached = this.cache.get(key);

    if (cached && (now - cached.timestamp) < cached.ttl) {
      return cached.value;
    }

    const result = fn();
    this.cache.set(key, { value: result, timestamp: now, ttl });
    return result;
  }

  /**
   * Mémoïse une fonction avec des arguments
   */
  memoizeWithArgs<T, Args extends any[]>(keyFn: (...args: Args) => string, fn: (...args: Args) => T, ttl: number = 300000) {
    return (...args: Args): T => {
      const key = keyFn(...args);
      return this.memoize(key, () => fn(...args), ttl);
    };
  }

  /**
   * Vide le cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Supprime une entrée spécifique du cache
   */
  clearKey(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Nettoie les entrées expirées
   */
  cleanExpired(): void {
    const now = Date.now();
    for (const [key, value] of this.cache.entries()) {
      if ((now - value.timestamp) >= value.ttl) {
        this.cache.delete(key);
      }
    }
  }
}
